import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Filter from "../components/Product/Filter/Filter";
import ProductList from "../components/Product/Body/ProductList";
import { getProductsByCategory, searchProducts } from "../Func/Products";
import {
  TextField,
  InputAdornment,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import debounce from "lodash/debounce";
import Fab from "@mui/material/Fab";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Zoom from "@mui/material/Zoom";
import SEO from "../components/SEO";

const SITE_URL = "https://holywearhouse.com";

export default function ProductCard() {
  const location = useLocation();
  // Extract the category from query parameters
  const queryParams = new URLSearchParams(location.search);
  const category = queryParams.get("category") || "All Products";
  const brandFromQuery = queryParams.get("brand");
  
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [sortBy, setSortBy] = useState("Recommended");
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [loading, setLoading] = useState(true); // 1. Add loading state
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const [isGiveawayMode, setIsGiveawayMode] = useState(
    queryParams.get("giveaway") === "true" && localStorage.getItem('giveawayMode') === 'true'
  );

  const sentinelRef = useRef(null);

  useEffect(() => {
    applyFilters();
    const handleScroll = () => {
      if (window.scrollY > 300) { // Show button after scrolling 300px
        setShowScrollTopButton(true);
      } else {
        setShowScrollTopButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    
    // Listen for giveaway mode changes
    const handleGiveawayModeChange = () => {
      const giveawayMode = localStorage.getItem('giveawayMode') === 'true';
      setIsGiveawayMode(giveawayMode && queryParams.get("giveaway") === "true");
    };

    window.addEventListener('giveawayModeChanged', handleGiveawayModeChange);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener('giveawayModeChanged', handleGiveawayModeChange);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    setPage(1);
  }, [filteredProducts]);

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        setPage((prev) => {
          // Only load more if there are more products
          if (prev * pageSize < filteredProducts.length) {
            return prev + 1;
          }
          return prev;
        });
      }
    },
    [filteredProducts.length]
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    };
    const observer = new window.IntersectionObserver(handleObserver, option);
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => {
      if (sentinelRef.current) observer.unobserve(sentinelRef.current);
    };
  }, [handleObserver]);

  const visibleProducts = filteredProducts.slice(0, page * pageSize);

  useEffect(() => {
    console.log(`Category: ${category}`);

    async function fetchProducts() {
      setLoading(true);
      // Fetch products based on the category
      const products = await getProductsByCategory(category);
      setProducts(products);

      if (brandFromQuery) {
        setSelectedBrands([brandFromQuery]);
        const filtered = products.filter(
          (product) => product.brand === brandFromQuery
        );
        console.log("Filtered products by brand:", products.length, filtered.length);
        setFilteredProducts(filtered);
      } else {
        setSelectedBrands([]);
        setFilteredProducts(products);
      }
      setLoading(false);
    }

    fetchProducts();
  }, []);

  const performSearch = async (query) => {
    if (query.trim().length === 0) {
      setFilteredProducts(products);
      return;
    }

    try {
      const results = await searchProducts(query);
      // Combine API results with existing products, avoiding duplicates
      const combined = [
        ...results,
        ...products.filter((p) => !results.some((r) => r.id === p.id)),
      ];
      setFilteredProducts(combined);
    } catch (err) {
      console.error("Search API error, falling back to keyword filter:", err);
      const fallback = products.filter(
        (product) =>
          product.title.toLowerCase().includes(query.toLowerCase()) ||
          product.brand.toLowerCase().includes(query.toLowerCase()) ||
          product.productType.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredProducts(fallback);
    }
  };

  // Debounced version of performSearch
  const debouncedSearch = debounce(performSearch, 400);

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleBrandFilter = (brands) => {
    setSelectedBrands(brands);
    applyFilters({ updatedBrands: brands });
  };

  const handlePriceRange = (range) => {
    setPriceRange(range);
    applyFilters({ updatedPriceRange: range });
  };

  const handleSortBy = (value) => {
    setSortBy(value);
    applyFilters({ updatedSortBy: value });
  };

  const applyFilters = ({
    updatedBrands = selectedBrands,
    updatedPriceRange = priceRange,
    updatedSortBy = sortBy,
  } = {}) => {
    let filtered = [...products];

    // Apply brand filter
    if (updatedBrands.length > 0) {
      filtered = filtered.filter((product) =>
        updatedBrands.includes(product.brand)
      );
    }

    // Apply price range filter
    filtered = filtered.filter(
      (product) =>
        product.price >= updatedPriceRange[0] &&
        product.price <= updatedPriceRange[1]
    );

    // Apply sort filter
    if (updatedSortBy === "Lowest → Highest Price") {
      filtered.sort((a, b) => a.price - b.price);
    } else if (updatedSortBy === "Highest → Lowest Price") {
      filtered.sort((a, b) => b.price - a.price);
    }

    setFilteredProducts(filtered);
  };

  const itemListStructuredData = useMemo(() => {
    if (!filteredProducts.length) {
      return null;
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${category} | Holy Wear House`,
      itemListElement: filteredProducts.slice(0, 20).map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: product.title,
        url: product.link || `${SITE_URL}/products?category=${encodeURIComponent(category)}`,
        image: product.imageUrl || product.images?.[0],
        brand: product.brand,
        offers: product.price
          ? {
              '@type': 'Offer',
              priceCurrency: 'USD',
              price: product.price,
              availability: 'https://schema.org/InStock',
            }
          : undefined,
      })),
    };
  }, [filteredProducts, category]);

  const breadcrumbStructuredData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Categories',
          item: `${SITE_URL}/categories`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: category,
          item: `${SITE_URL}/products${
            category !== 'All Products'
              ? `?category=${encodeURIComponent(category)}`
              : ''
          }`,
        },
      ],
    }),
    [category]
  );

  return (
    <>
      <SEO
        title={`${category} | Faith-Based Apparel from Holy Wear House`}
        description={`Shop ${category.toLowerCase()} from top Christian clothing brands, curated by Holy Wear House with transparent pricing and quality.`}
        keywords={`christian ${category.toLowerCase()}, faith-based ${category.toLowerCase()}, christian apparel, holy wear house products`}
        path={`/products${category !== "All Products" ? `?category=${encodeURIComponent(category)}` : ""}`}
        structuredData={[itemListStructuredData, breadcrumbStructuredData].filter(Boolean)}
      />
      {/* search input */}
      <Box sx={{ pl: "38px" }}>

      <TextField
        variant="outlined"
        size="small"
        width="100%"
        py="20px"
        placeholder="What are you looking for?"
        onChange={handleSearch}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment sx={{ marginLeft: "8px" }} position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: 1,
          mt: 2,
          width: "90%",
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: "black.100",
            height: 36,
            padding: 0,
          },
          "& .MuiOutlinedInput-input": {
            padding: "6px 8px",
          },
        }}
      />
      </Box>
      <Filter
        onSortBy={handleSortBy}
        onPriceRange={handlePriceRange}
        onBrandFilter={handleBrandFilter}
      />
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <ProductList products={visibleProducts} />
          {/* Sentinel for infinite scroll */}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {/* Optional: Loading spinner when more items are available */}
          {visibleProducts.length < filteredProducts.length && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
                background: "#f6f5f3",
                zIndex: 10,
                position: "relative",
                py: 6,
              }}
            >
              <CircularProgress size={64} thickness={5} color="primary" />
            </Box>
          )}
        </>
      )}
      <Zoom in={showScrollTopButton}>
        <Box
          onClick={scrollToTop}
          role="presentation"
          sx={{ position: "fixed", bottom: 27, right: 16, zIndex: 100, }}
        >
          <Fab color="black" size="small" aria-label="scroll back to top"
            sx={{
              backgroundColor:"#f6f5f3"
            }}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        </Box>
      </Zoom>
    </>
  );
}
